import { useState } from 'react';
import { MessageSquareText, Trash2 } from 'lucide-react';
import type MiniSearch from 'minisearch';
import { scaleNutrients } from '@/engine/portions';
import type { MealSlot } from '@/engine/types';
import { addMealEntry, defaultSlot, SLOTS } from '@/features/diary/useDiary';
import type { FoodRow } from '@/lib/db';
import { num } from '@/lib/format';
import { parseMealText } from '@/lib/llm/client';
import { resolveMealItems, toGrams, type ResolvedItem } from '@/lib/llm/parseMeal';
import type { SearchDoc } from '@/lib/search';
import { inputClass } from '@/components/ui/Field';
import { cn } from '@/lib/utils';

interface Props {
  date: string;
  index: MiniSearch<SearchDoc>;
  byId: ReadonlyMap<string, FoodRow>;
  onDone: () => void;
}

type State =
  | { kind: 'escribiendo' }
  | { kind: 'pensando' }
  | { kind: 'revisando'; items: ResolvedItem[]; leftover: number }
  | { kind: 'no_disponible' };

/**
 * Registro por texto libre (SPEC 6.B, solo con la capa LLM activada).
 *
 * Nada se registra sin pasar por la revisión: el LLM propone, el usuario
 * confirma. Lo que no se ha podido emparejar con el catálogo se enseña marcado
 * en vez de adivinarse; el alimento propuesto se puede cambiar por otro
 * candidato y los gramos son editables antes de añadir.
 */
export function FreeTextMeal({ date, index, byId, onDone }: Props) {
  const [text, setText] = useState('');
  const [state, setState] = useState<State>({ kind: 'escribiendo' });
  const [slot, setSlot] = useState<MealSlot>(defaultSlot());
  const [saving, setSaving] = useState(false);

  async function interpret() {
    if (text.trim().length < 3) return;
    setState({ kind: 'pensando' });

    const outcome = await parseMealText(text);
    if (!outcome.available) {
      // Degradación silenciosa: no se explica por qué ha fallado el proveedor,
      // solo se ofrece la vía de siempre, que es el buscador.
      setState({ kind: 'no_disponible' });
      return;
    }

    setState({
      kind: 'revisando',
      items: resolveMealItems(outcome.value, index, byId),
      leftover: 0,
    });
  }

  function update(position: number, change: (item: ResolvedItem) => ResolvedItem) {
    if (state.kind !== 'revisando') return;
    setState({ ...state, items: state.items.map((it, i) => (i === position ? change(it) : it)) });
  }

  function setGrams(position: number, grams: number) {
    update(position, (item) => ({
      ...item,
      grams,
      problem: grams > 0 ? undefined : item.problem,
    }));
  }

  function setFood(position: number, foodId: string) {
    update(position, (item) => {
      const food = item.alternatives.find((f) => f.id === foodId) ?? item.food;
      const grams = toGrams(item.raw, food);
      return grams != null && grams > 0
        ? { ...item, food, grams, problem: undefined }
        : { ...item, food, grams: null, problem: 'sin_cantidad' };
    });
  }

  function remove(position: number) {
    if (state.kind !== 'revisando') return;
    setState({ ...state, items: state.items.filter((_, i) => i !== position) });
  }

  async function addAll() {
    if (state.kind !== 'revisando') return;
    setSaving(true);
    try {
      const ready = state.items.filter((i) => i.food && i.grams && i.grams > 0);
      for (const item of ready) {
        await addMealEntry({ date, slot, food: item.food!, grams: item.grams! });
      }
      // Lo que aún no se puede añadir NO se tira: se queda en la lista para
      // completarlo (gramos) o buscarlo a mano, y se dice cuántos quedan.
      const pending = state.items.filter((i) => !ready.includes(i));
      if (pending.length === 0) {
        setText('');
        setState({ kind: 'escribiendo' });
        onDone();
      } else {
        setState({ kind: 'revisando', items: pending, leftover: pending.length });
      }
    } finally {
      setSaving(false);
    }
  }

  const ready = state.kind === 'revisando' && state.items.some((i) => i.food && i.grams);

  return (
    <section
      aria-labelledby="texto-libre"
      className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-[var(--color-border)] p-3"
    >
      <h2 id="texto-libre" className="flex items-center gap-2 text-sm font-semibold">
        <MessageSquareText aria-hidden="true" className="size-4" />
        Escribe lo que has comido
      </h2>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        readOnly={state.kind === 'revisando'}
        rows={2}
        maxLength={500}
        placeholder="200 g de arroz con pollo y una manzana"
        aria-label="Describe tu comida"
        className={cn(inputClass, 'min-h-[72px] py-2')}
      />

      {state.kind === 'no_disponible' ? (
        <p role="status" className="text-xs text-[var(--color-text-muted)]">
          Ahora mismo no se puede interpretar el texto. Búscalo en el buscador como siempre.
        </p>
      ) : null}

      {state.kind === 'revisando' ? (
        <>
          {state.leftover > 0 ? (
            <p role="status" className="text-xs text-[var(--color-text-muted)]">
              Añadido lo que estaba completo. Quedan {state.leftover}: indica los gramos o búscalo
              en el buscador.
            </p>
          ) : null}

          <ul className="flex flex-col gap-1.5" aria-label="Revisa antes de añadir">
            {state.items.map((item, position) => (
              <ReviewRow
                key={`${item.raw.alimento}-${position}`}
                item={item}
                onGrams={(g) => setGrams(position, g)}
                onFood={(id) => setFood(position, id)}
                onRemove={() => remove(position)}
              />
            ))}
          </ul>

          <fieldset>
            <legend className="mb-1 text-xs font-medium">Franja</legend>
            <div className="flex flex-wrap gap-1.5">
              {SLOTS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  role="radio"
                  aria-checked={slot === s.value}
                  onClick={() => setSlot(s.value)}
                  className={cn(
                    'tap rounded-full border px-3 text-xs font-medium',
                    slot === s.value
                      ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-muted)]',
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setState({ kind: 'escribiendo' })}
              className="tap flex-1 rounded-xl border border-[var(--color-border)] px-3 text-sm font-medium"
            >
              Volver a escribir
            </button>
            <button
              type="button"
              onClick={() => void addAll()}
              disabled={!ready || saving}
              className="tap flex-1 rounded-xl bg-[var(--color-brand-button)] px-3 text-sm font-semibold text-[var(--color-on-brand)] disabled:opacity-50"
            >
              Añadir lo revisado
            </button>
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => void interpret()}
          disabled={text.trim().length < 3 || state.kind === 'pensando'}
          className="tap rounded-xl border border-[var(--color-border)] px-4 text-sm font-medium disabled:opacity-50"
        >
          {state.kind === 'pensando' ? 'Interpretando…' : 'Interpretar'}
        </button>
      )}
    </section>
  );
}

function ReviewRow({
  item,
  onGrams,
  onFood,
  onRemove,
}: {
  item: ResolvedItem;
  onGrams: (grams: number) => void;
  onFood: (id: string) => void;
  onRemove: () => void;
}) {
  const label = item.food?.name ?? item.raw.alimento;
  const kcal = item.food && item.grams ? scaleNutrients(item.food.per100g, item.grams).kcal : null;

  return (
    <li
      className={cn(
        'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm',
        item.problem ? 'bg-[var(--color-warn-soft)]' : 'bg-[var(--color-surface-2)]',
      )}
    >
      <div className="min-w-0 flex-1">
        {item.food && item.alternatives.length > 1 ? (
          <select
            value={item.food.id}
            onChange={(e) => onFood(e.target.value)}
            aria-label={`Alimento para «${item.raw.alimento}»`}
            className="w-full truncate rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5 text-sm"
          >
            {item.alternatives.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        ) : (
          <p className="truncate">{label}</p>
        )}
        <p className="text-xs text-[var(--color-text-muted)]">
          {item.problem === 'no_encontrado'
            ? 'No está en el catálogo: búscalo en el buscador'
            : item.problem === 'sin_cantidad'
              ? `«${num(item.raw.cantidad)} ${item.raw.unidad}»: indica los gramos`
              : item.food && item.grams
                ? // null ≠ 0: si no se sabe la energía, se dice, no se pinta un 0.
                  `${kcal == null ? '—' : num(kcal)} kcal`
                : ''}
        </p>
      </div>
      {item.food ? (
        <input
          type="number"
          inputMode="numeric"
          min={1}
          aria-label={`Gramos de ${item.food.name}`}
          value={item.grams ?? ''}
          onChange={(e) => onGrams(Number(e.target.value))}
          className="tap w-20 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-right text-sm tabular"
        />
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Quitar ${label}`}
        className="tap rounded-lg px-1 text-[var(--color-text-muted)]"
      >
        <Trash2 aria-hidden="true" className="size-4" />
      </button>
    </li>
  );
}
